//@flow
import EntityImportDataHelper from 'common/entity/helpers/EntityImportDataHelper';
import I18nHelper from 'common/helpers/I18nHelper';
import { TeamId } from 'common/models/internal/TeamId';
import { TimeSlot } from 'common/models/internal/TimeSlot';
import DataUtils from 'common/utils/DataUtils';
import NumberUtils from 'common/utils/NumberUtils';
import { DayOfWeekType } from 'common/enums/DateTimeEnums';
import { cloneDeep } from 'lodash';
import { Shift } from 'common/models/internal/Shift';
import { type List } from 'immutable';
import { EntityImportHelper } from 'common/entity/helpers/EntityImportHelper';
import * as Immutable from 'immutable';
import DateTimeUtils, {
    CustomDateFormats,
    DateInputFormats
} from 'common/utils/DateTimeUtils';

const translationsNamespace = ['validations', 'entityManagement'];
const nsTranslate = I18nHelper.getTranslate(translationsNamespace);
const daysList = Object.values(DayOfWeekType);
const timeRegex = new RegExp('([01]?[0-9]|2[0-3]):[0-5][0-9]$');

const translatedDays = daysList.map(day =>
    nsTranslate(`entityManagement:shiftTable.days.${day}`)
);

export class ShiftImportHelper extends EntityImportDataHelper {
    static processTeam({ cellValue, clientId }) {
        return cellValue
            ? {
                  teamId: cellValue.trim(),
                  clientId
              }
            : null;
    }

    static validateWorkingDaysLocation({ entity, header }) {
        const dayOfWeek = header.accessor.get(1); // day of week
        const locationType = header.accessor.get(2); // start or end
        const latLngType = header.getFieldAccessor(); // lat or lng
        const { workingDays } = entity;
        const workingDay = workingDays.find(
            workingDay => workingDay.dayOfWeek === dayOfWeek
        );
        const latLng = workingDay
            ? workingDay.getIn([locationType, 'latLng'])
            : null;
        return latLng && latLng.getValidationMessage(latLngType);
    }

    static validateStartLocationAddressLatlng({ entity }) {
        const { startLocation } = entity;
        return ShiftImportHelper.validateAddressLatLng(startLocation);
    }

    static validateEndLocationAddressLatlng({ entity }) {
        const { endLocation } = entity;
        return ShiftImportHelper.validateAddressLatLng(endLocation);
    }

    static validateAddressLatLng(location) {
        if (location) {
            const { address, latLng } = location;
            if (address && !latLng) {
                return nsTranslate(
                    'shiftValidationErrors.addessLatLngRequired'
                );
            }
        }
    }

    static validateWorkingDaysBreak({ entity, header }) {
        const [dayOfWeek] = header.id.split('.');
        const { workingDays } = entity;
        const workingDay = workingDays.find(
            workingDay => workingDay.dayOfWeek === dayOfWeek
        );
        return (
            workingDay?.getValidationMessage('breaks') ||
            workingDay?.validateBreaksWithSlot()
        );
    }

    static validateWorkingDaysTimeSlot({ entity, header }) {
        const [dayOfWeek] = header.id.split('.');
        const { workingDays } = entity;
        const workingDay = workingDays.find(
            workingDay => workingDay.dayOfWeek === dayOfWeek
        );
        const isStartTimeInvalid =
            workingDay?.slot.start && !workingDay.slot.start?.includes(':');
        const isEndTimeInvalid =
            workingDay?.slot.end && !workingDay.slot.end?.includes(':');
        const isOffsetInvalid =
            workingDay?.slot.start &&
            workingDay?.slot.end &&
            DataUtils.isEmpty(workingDay.endDayOffset);
        if (isStartTimeInvalid || isEndTimeInvalid || isOffsetInvalid) {
            // when slot is filled but not in format
            return nsTranslate('shiftValidationErrors.invalidTimeFormat');
        }

        const error = entity.getValidationMessage('workingDays');
        if (error) {
            return error;
        }
        return workingDay && workingDay.getValidationMessage('slot');
    }

    static serializeTeam = (team: Object): TeamId => TeamId.fromJS(team);

    static validateTeamId({ teams, entity }) {
        if (!entity.teamId) {
            return null;
        }
        const validTeams = EntityImportDataHelper.getActiveTeamsList(teams);
        return TeamId.getTeamIdsValidationMessage({
            validTeams,
            teamIds: Immutable.List([entity.teamId])
        });
    }

    static getEndDayOffset = (
        slotDay: $Values<typeof DayOfWeekType>,
        endDay: $Values<typeof DayOfWeekType>
    ): ?number => {
        if (!slotDay || !endDay) {
            return;
        }
        const tranlatedSlotDay = nsTranslate(
            `entityManagement:shiftTable.days.${slotDay}`
        );
        const translatedEndDayIndex = translatedDays.indexOf(endDay);
        if (translatedEndDayIndex < 0) {
            return;
        }
        const endDayOffset =
            (translatedEndDayIndex -
                translatedDays.indexOf(tranlatedSlotDay) +
                7) %
            7;
        return endDayOffset;
    };

    static processTimeSlot({
        cellValue,
        slotDay
    }: {
        cellValue: ?string,
        slotDay: $Values<typeof DayOfWeekType>
    }): {
        endDayOffset: ?number,
        dateObj: {
            start: ?string,
            end: ?string
        }
    } {
        const [start, end] = cellValue?.split('-') ?? [];
        let dateObj = {};
        if (start && end) {
            const endDay = end.match(/\((.*)\)/)?.[1];
            const startDay = start.match(/\((.*)\)/)?.[1];
            const isValidStartDay =
                startDay ===
                nsTranslate(`entityManagement:shiftTable.days.${slotDay}`);
            const startTime = start.split(')')[1] || start;
            const endTime = end.split(')')[1] || end;
            const isValidStartTime = timeRegex.test(startTime);
            const isValidEndTime = timeRegex.test(endTime);
            const slotStartTime =
                (isValidStartTime && isValidStartDay && startTime?.trim()) ||
                ' '; // Hack to invalidate the slot start time
            const slotEndTime = (isValidEndTime && endTime?.trim()) || ' ';
            dateObj = EntityImportDataHelper.getSlots(
                slotStartTime,
                slotEndTime
            );
            const endDayOffset = ShiftImportHelper.getEndDayOffset(
                slotDay,
                endDay
            );
            return { endDayOffset, dateObj };
        }
        if (!('start' in dateObj) && cellValue) {
            dateObj.start = cellValue;
        }
        return { dateObj };
    }

    static getTimeSlotsAccessor({
        accessor,
        entity,
        workingDaysAccessor,
        dayOfWeekIndex
    }: {
        accessor: List<string>,
        entity: Shift,
        workingDaysAccessor: Array<string>,
        dayOfWeekIndex: number
    }) {
        const dayOfWeek = accessor.get(dayOfWeekIndex);
        const dayIndex = entity
            .getIn(workingDaysAccessor)
            .findIndex(workingDay => dayOfWeek === workingDay.get('dayOfWeek'));
        return accessor.set(dayOfWeekIndex, dayIndex);
    }

    static serializeTimeSlot = (
        slot: Object,
        { entity, accessor }
    ): TimeSlot => {
        const { dateObj, endDayOffset } = ShiftImportHelper.processTimeSlot({
            cellValue: slot,
            slotDay: entity.getIn(accessor).dayOfWeek
        });
        return entity
            .getIn(accessor)
            .setSlot(TimeSlot.fromJS(dateObj))
            .setEndDayOffset(endDayOffset);
    };

    static _processTimeSlotToShiftInstant({ slot, duration, dayOfWeek }) {
        let [start, end] = slot && slot.split('-');
        if (start && end) {
            const trimmedStart = start.trim();
            const trimmedEnd = end.trim();
            const indexOfClosingBracketInStart = trimmedStart.indexOf(')');
            const indexOfClosingBracketInEnd = trimmedEnd.indexOf(')');
            const startTime = trimmedStart.substring(
                indexOfClosingBracketInStart + 1
            );
            const startDay = trimmedStart.substring(
                1,
                indexOfClosingBracketInStart
            );
            const endTime = trimmedEnd.substring(
                indexOfClosingBracketInEnd + 1
            );
            const endDay = trimmedEnd.substring(1, indexOfClosingBracketInEnd);
            start =
                DateTimeUtils.customFormat(
                    startTime,
                    CustomDateFormats.API_TIME,
                    DateInputFormats.HOUR24_SHORT
                ) || start;
            end =
                DateTimeUtils.customFormat(
                    endTime,
                    CustomDateFormats.API_TIME,
                    DateInputFormats.HOUR24_SHORT
                ) || end;
            return {
                startShiftInstant: {
                    time: start,
                    day:
                        1 +
                        ShiftImportHelper.getEndDayOffset(dayOfWeek, startDay)
                },
                endShiftInstant: {
                    time: end,
                    day:
                        1 + ShiftImportHelper.getEndDayOffset(dayOfWeek, endDay)
                },
                duration
            };
        }
        return {
            startShiftInstant: {
                time: start,
                day: 1
            },
            endShiftInstant: {
                time: end,
                day: 1
            },
            duration
        };
    }

    static processBreaks({ cellValue, dayOfWeek }) {
        if (!DataUtils.isEmpty(cellValue)) {
            let defaultDuration = 10; // 10 minutes
            const breakSlots = cellValue.split(',').map(breakSlot => {
                let breakSlotWithoutDuration = breakSlot;
                if (breakSlot.split(':').length > 3) {
                    const breakDurationIndex = breakSlot.lastIndexOf(':');
                    breakSlotWithoutDuration = breakSlot.substring(
                        0,
                        breakDurationIndex
                    );
                    const breaksDuration = breakSlot.split(':').pop();
                    defaultDuration =
                        breakSlot.split(':').length > 3 &&
                        !DataUtils.isEmpty(breaksDuration)
                            ? breaksDuration
                            : defaultDuration;
                }
                return this._processTimeSlotToShiftInstant({
                    slot: breakSlotWithoutDuration,
                    duration: NumberUtils.isNumeric(defaultDuration)
                        ? defaultDuration * 60
                        : defaultDuration,
                    dayOfWeek
                });
            });
            return cloneDeep(breakSlots);
        }
        return null;
    }

    static processEntityRows = ({
        rows
    }: {
        rows: Immutable.List<EntityImportRowModelType>
    }): Immutable.List<Shift> =>
        rows.map(entityRow => {
            const updatedShiftRow = entityRow.map((groupValue, headerId) => {
                switch (headerId) {
                    case 'workingDays':
                        return groupValue
                            .map((workingDay, dayOfWeek) => {
                                const {
                                    dateObj,
                                    endDayOffset
                                } = ShiftImportHelper.processTimeSlot({
                                    cellValue: workingDay.get('slot'),
                                    slotDay: dayOfWeek
                                });
                                return workingDay
                                    .set('dayOfWeek', dayOfWeek)
                                    .set('endDayOffset', endDayOffset)
                                    .set('slot', dateObj);
                            })
                            .toList();
                    default:
                        return groupValue;
                }
            });
            return Shift.fromJS(updatedShiftRow.toJS());
        });

    static getUploadedEntities = ({
        uploadedTable,
        clientId,
        getDataForCell
    }): Immutable.List<Shift> => {
        const shiftsData = EntityImportHelper.makeDataForEntities({
            table: uploadedTable,
            clientId,
            getDataForCell,
            processEntityRows: ShiftImportHelper.processEntityRows
        });
        return Immutable.List(shiftsData);
    };

    static generateEntityList = (shiftsData: Array<*>): Immutable.List<Shift> =>
        Immutable.List(shiftsData.map(shiftData => Shift.fromJS(shiftData)));
}

export default ShiftImportHelper;
